/**
 * src/features/auth/ui/SecuritySettingsCard.tsx
 *
 * Phase 43 (PWD-08) — the "Sign-in & Security" card mounted inside SystemView. This is the
 * first UI path in the product that reaches POST /api/user/password at all, and it is also
 * the exact double-click surface that makes roadmap criterion 5's duplicate-credential-row
 * race reachable — see the inFlightRef guard below, the client half of that fix.
 *
 * State is one explicit discriminated union (`CardMode`), not a scatter of booleans:
 *   - 'collapsed': header + one call-to-action button, no password input rendered at all.
 *   - 'set': the first-time set-password form (one field, POST /api/user/password).
 *   - 'change': the change-password form (two fields, authClient.changePassword). Reached
 *     either by a 409 PASSWORD_ALREADY_SET pivot from 'set' (pivoted: true — shows the
 *     already-set hint) or directly, when the store already knows hasPassword===true at
 *     mount (pivoted: false — no hint, the card already knew).
 *
 * This surface deliberately reuses SystemView's OWN raw-Tailwind card idiom (icon chip +
 * title + subtitle, raw <button> CTA), not Phase 42's text-body/text-caption tokens —
 * importing a second typography system into one screen is exactly what 43-UI-SPEC.md's
 * Typography section forbids. The password-field internals (static show/hide toggle,
 * independent Caps Lock hint, autoComplete pairing) come from the shared PasswordField.tsx
 * (WR-03, 43-REVIEW.md) — this file no longer defines its own local copy.
 */

import { FC, FormEvent, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyRound, CheckCircle2 } from 'lucide-react';
import { authClient } from '@/lib/auth-client';
import { useAuthStore } from '@/stores/useAuthStore';
import { MIN_PASSWORD_LENGTH } from '../lib/passwordPolicy';
import { broadcastSessionRevoked } from '../lib/authBroadcast';
import { PasswordField } from './PasswordField';

type CardMode =
  | { kind: 'collapsed' }
  | { kind: 'set' }
  | { kind: 'change'; pivoted: boolean };

const ctaButtonClass =
  'w-full py-4 bg-blue-600 text-white rounded-2xl font-bold shadow-lg shadow-blue-100 dark:shadow-none hover:bg-blue-700 active:scale-95 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed';

export const SecuritySettingsCard: FC = () => {
  const { t } = useTranslation();
  const hasPasswordAtMount = useAuthStore((s) => s.hasPassword);

  const [mode, setMode] = useState<CardMode>({ kind: 'collapsed' });
  // Eager initial state (polish on top of the required 409 pivot, which stays the safety
  // net for the case where a password was set in another tab after mount): if the store
  // already knows hasPassword===true, the collapsed CTA reads the change label and
  // expanding opens the two-field form directly.
  const [hasPasswordKnown, setHasPasswordKnown] = useState(() => hasPasswordAtMount === true);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Synchronous in-flight guard — literal reuse of the PasswordSignInForm.tsx idiom and the
  // exact same reason (React state batching would miss a same-tick double submit). This is
  // the client half of roadmap criterion 5; this button is the exact surface that makes the
  // duplicate-credential-row race reachable.
  const inFlightRef = useRef(false);

  const handleExpand = () => {
    setErrorMessage(null);
    setSuccessMessage(null);
    setMode(hasPasswordKnown ? { kind: 'change', pivoted: false } : { kind: 'set' });
  };

  const errorText = t('system.security.error', "Couldn't update your password. Please try again.");

  const handleSetSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (inFlightRef.current) return;
    if (newPassword.length < MIN_PASSWORD_LENGTH) return;

    inFlightRef.current = true;
    setIsLoading(true);
    setErrorMessage(null);

    try {
      // The project's own wrapper route, not a better-auth client method — its failures
      // arrive as HTTP statuses, not as a { data, error } pair.
      const res = await fetch('/api/user/password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newPassword }),
      });

      if (res.ok) {
        setHasPasswordKnown(true);
        setSuccessMessage(
          t('system.security.set_success', 'Password set. You can now sign in with it.'),
        );
        setNewPassword('');
        setMode({ kind: 'collapsed' });
      } else if (res.status === 409) {
        // 409 PASSWORD_ALREADY_SET is a state signal, not an error — pivot the form in
        // place, keep the already-typed new password, never render role="alert" here.
        setHasPasswordKnown(true);
        setMode({ kind: 'change', pivoted: true });
      } else {
        setErrorMessage(errorText);
      }
    } catch {
      setErrorMessage(errorText);
    } finally {
      inFlightRef.current = false;
      setIsLoading(false);
    }
  };

  const handleChangeSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (inFlightRef.current) return;
    if (!currentPassword || newPassword.length < MIN_PASSWORD_LENGTH) return;

    inFlightRef.current = true;
    setIsLoading(true);
    setErrorMessage(null);

    // WR-01 pattern: better-auth's client returns { data, error } and never throws.
    const result = await authClient.changePassword({
      currentPassword,
      newPassword,
      // Explicit-over-default, same principle as resetPasswordTokenExpiresIn in
      // functions/lib/auth.ts — a library default could change between versions.
      revokeOtherSessions: true,
    });

    if (!result.error) {
      setHasPasswordKnown(true);
      setSuccessMessage(
        t('system.security.change_success', 'Password changed. Other devices have been signed out.'),
      );
      setCurrentPassword('');
      setNewPassword('');
      setMode({ kind: 'collapsed' });
      // PWD-06: announce the revocation to OTHER tabs on the shared estimatix-auth channel.
      // This tab does NOT need to guard itself explicitly — the origin id inside the
      // message does that in useAuth.ts's listener — but the reason it must not see its
      // own notice is worth stating: changePassword hands THIS tab a freshly rotated
      // session (update-user.d.mts confirms a new token in the response), so it is the one
      // tab that is still legitimately signed in. Deliberately NOT called from the
      // first-time set-password success path above — setting a first password revokes no
      // session, so announcing one there would sign other tabs out for nothing.
      broadcastSessionRevoked();
    } else {
      // Same single message for a wrong current password as for any other failure —
      // extends the AUTH-11 indistinguishable-failure principle to password changes.
      setErrorMessage(errorText);
    }

    inFlightRef.current = false;
    setIsLoading(false);
  };

  const collapsedCtaLabel = hasPasswordKnown
    ? t('system.security.change_cta', 'Change password')
    : t('system.security.set_cta', 'Set a password');

  return (
    <div className="bg-white dark:bg-slate-900 p-6 rounded-3xl border border-slate-100 dark:border-slate-800 shadow-sm space-y-6">
      <div className="flex items-center gap-3 mb-2">
        <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-xl text-blue-600 dark:text-blue-400">
          <KeyRound size={24} />
        </div>
        <div>
          <h3 className="font-bold text-slate-800 dark:text-slate-100 text-lg">
            {t('system.security.title', 'Sign-in & Security')}
          </h3>
          <p className="text-xs text-slate-400 dark:text-slate-500 font-medium">
            {t('system.security.subtitle', 'Manage how you sign in to your account')}
          </p>
        </div>
      </div>

      {mode.kind === 'collapsed' && (
        <>
          <button type="button" onClick={handleExpand} className={ctaButtonClass}>
            {collapsedCtaLabel}
          </button>
          {successMessage && (
            <p role="status" className="flex items-center gap-2 text-body text-slate-600 dark:text-slate-400">
              <CheckCircle2 size={18} className="text-green-600 dark:text-green-400 shrink-0" aria-hidden="true" />
              {successMessage}
            </p>
          )}
        </>
      )}

      {mode.kind === 'set' && (
        <form onSubmit={handleSetSubmit} className="flex flex-col gap-3">
          <PasswordField
            value={newPassword}
            onChange={setNewPassword}
            label={t('system.security.new_password_label', 'New password')}
            autoComplete="new-password"
            disabled={isLoading}
          />
          <button
            type="submit"
            disabled={isLoading || newPassword.length < MIN_PASSWORD_LENGTH}
            aria-busy={isLoading}
            className={ctaButtonClass}
          >
            {t('system.security.set_cta', 'Set a password')}
          </button>
          {errorMessage && (
            <p role="alert" className="text-body text-red-600 dark:text-red-400">
              {errorMessage}
            </p>
          )}
        </form>
      )}

      {mode.kind === 'change' && (
        <form onSubmit={handleChangeSubmit} className="flex flex-col gap-3">
          {/* Current password ABOVE new password — required document order for the 409
              pivot (a test asserts this ordering via compareDocumentPosition/index). */}
          <PasswordField
            value={currentPassword}
            onChange={setCurrentPassword}
            label={t('system.security.current_password_label', 'Current password')}
            autoComplete="current-password"
            disabled={isLoading}
          />
          <PasswordField
            value={newPassword}
            onChange={setNewPassword}
            label={t('system.security.new_password_label', 'New password')}
            autoComplete="new-password"
            disabled={isLoading}
          />
          {mode.pivoted && (
            <p role="status" className="text-body text-slate-600 dark:text-slate-400">
              {t('system.security.already_set_hint', 'You already have a password — enter it to change.')}
            </p>
          )}
          <button
            type="submit"
            disabled={isLoading || !currentPassword || newPassword.length < MIN_PASSWORD_LENGTH}
            aria-busy={isLoading}
            className={ctaButtonClass}
          >
            {t('system.security.change_cta', 'Change password')}
          </button>
          {errorMessage && (
            <p role="alert" className="text-body text-red-600 dark:text-red-400">
              {errorMessage}
            </p>
          )}
        </form>
      )}
    </div>
  );
};
